import { Link } from 'react-router-dom';
import { useTranslation } from '@doccareplus/shared/lib';
import { Button } from './Button';
import { BackButton } from './PageHeader';

/**
 * Premium 404 Not Found Page with dynamic animations and medical brand themed styling.
 */
export function NotFoundPage() {
  const { t } = useTranslation('common');

  return (
    <div className="relative min-h-screen w-full flex flex-col items-center justify-center bg-[var(--bg)] px-6 overflow-hidden select-none">
      
      {/* 1. Medical Grid Overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05]"
        style={{
          backgroundImage: `
            linear-gradient(var(--border) 1px, transparent 1px),
            linear-gradient(90deg, var(--border) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px',
        }}
      />

      {/* 2. Premium Radial Aura */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] md:w-[600px] h-[350px] md:h-[600px] bg-gradient-to-br from-brand-400/10 via-primary/5 to-transparent rounded-full blur-[80px] md:blur-[120px] pointer-events-none z-0" />

      {/* 3. Floating Card Container */}
      <div className="w-full max-w-[460px] bg-[var(--surface)] border border-[var(--border)] rounded-[20px] p-8 md:p-10 shadow-[var(--shadow-3)] z-10 flex flex-col items-center text-center animate-[popIn_0.3s_ease-out] relative">
        
        {/* Animated Medical Graphic (Pulse / Radar scanner) */}
        <div className="relative mb-8 flex items-center justify-center">
          {/* Pulsing glow rings */}
          <div className="absolute w-[130px] h-[130px] rounded-full border border-brand-400/20 animate-ping opacity-70" />
          <div className="absolute w-[160px] h-[160px] rounded-full border border-brand-500/10 animate-[pulse_3s_infinite]" />
          
          {/* Scanner disk */}
          <div className="relative w-[100px] h-[100px] rounded-full bg-[var(--surface-2)] border border-[var(--border-strong)] flex items-center justify-center shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
            {/* Radar scanner line */}
            <div className="absolute inset-0 rounded-full animate-[spin_4s_linear_infinite] after:content-[''] after:absolute after:top-0 after:left-1/2 after:w-[2px] after:h-1/2 after:bg-gradient-to-b after:from-brand-500 after:to-transparent" />
            
            {/* Center Heartbeat EKG sweep */}
            <svg 
              className="w-16 h-12 text-[var(--primary)] z-10 relative" 
              viewBox="0 0 100 50" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2.5"
            >
              <path
                d="M 5 25 L 30 25 L 37 10 L 45 40 L 52 25 L 65 25 L 72 5 L 80 45 L 87 25 L 95 25"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  strokeDasharray: '200',
                  strokeDashoffset: '200',
                  animation: 'ekg-sweep 3s linear infinite'
                }}
              />
            </svg>
          </div>
        </div>

        {/* 404 Number with Premium Gradient */}
        <h1 className="text-7xl md:text-8xl font-extrabold tracking-tighter bg-gradient-to-r from-brand-400 via-[var(--primary)] to-brand-600 bg-clip-text text-transparent drop-shadow-sm select-none">
          404
        </h1>

        {/* Header and description */}
        <h2 className="mt-4 text-[18px] md:text-[20px] font-bold text-[var(--text)] tracking-tight">
          {t('notFound.title')}
        </h2>
        <p className="mt-3 text-[13px] text-[var(--text-3)] leading-relaxed max-w-[340px]">
          {t('notFound.description')}
        </p>

        {/* Actions */}
        <div className="mt-8 w-full flex flex-col items-center gap-4 border-t border-[var(--border)] pt-6">
          <Link to="/" className="w-full">
            <Button variant="primary" fullWidth className="h-10 text-[13px] font-bold rounded-[9px] shadow-sm">
              {t('notFound.backToDashboard')}
            </Button>
          </Link>
          <BackButton
            label={t('notFound.goBack') || 'Go back to previous page'}
            onClick={() => window.history.back()}
            className="mb-0 text-xs font-semibold text-[var(--text-3)] hover:text-[var(--text)] focus:outline-none"
          />
        </div>
      </div>

      {/* Local keyframe styles for EKG pulse */}
      <style>{`
        @keyframes ekg-sweep {
          0% {
            stroke-dashoffset: 200;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </div>
  );
}
